import React, { memo, useCallback, useMemo, useEffect, useState, Suspense } from 'react'
import { useSelector } from 'react-redux'
import {
  ActivityTimeline,
  ActivityTimelineContext,
  ActivityTimelineContextProps,
  selectTimelineError,
  selectTimelineData,
  selectTimelineIsLoading,
  type ActivityInfo,
} from '../../../../../../components/v2/activity-timeline'
import { EditHrEmployeeEventModal } from '../../modals/edit-hr-employee-event-modal'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'
import { deleteTimeLineItem, setTimelineClear } from '../../../../../../store/timeline/actions'

interface TabTimelineProps {
  employeeId: string | number
  loading?: boolean
}

export const TabTimeline: React.FC<TabTimelineProps> = memo((props: TabTimelineProps) => {
  const { employeeId, loading = false } = props
  const dispatch = useAppDispatch()
  const timelineIsLoading = useSelector(selectTimelineIsLoading)
  const timelineError = useSelector(selectTimelineError)
  const timelineData = useSelector(selectTimelineData)
  const [editEvent, setEditEvent] = useState<ActivityInfo | null>(null)

  useEffect(() => {
    // Очищаем данные timeline при изменении employeeId, чтобы избежать отображения данных от предыдущего сотрудника
    dispatch(setTimelineClear())

    // Защита от дублирования запросов полностью на уровне action creator
    // Просто отправляем запрос, action creator сам проверит и предотвратит дублирование
    if (employeeId && employeeId !== 'new') {
      try {
        dispatch(getTimeLineData('hr_employee', employeeId))
      } catch (error) {
        console.error('Error dispatching getTimeLineData:', error)
      }
    }

    // Cleanup: очищаем данные при размонтировании компонента
    return () => {
      dispatch(setTimelineClear())
    }
  }, [employeeId, dispatch])

  const deleteTimelineItem = useCallback(
    activityItem => {
      try {
        dispatch(deleteTimeLineItem(activityItem))
      } catch (error) {
        console.error('Error deleting timeline item:', error)
      }
    },
    [dispatch],
  )

  const onEditHrEmployeeEvent = useCallback((data: ActivityInfo) => {
    setEditEvent(data)
  }, [])

  const handleEventSaved = useCallback(() => {
    setEditEvent(null)
    dispatch(setTimelineClear())
    if (employeeId && employeeId !== 'new') {
      dispatch(getTimeLineData('hr_employee', employeeId))
    }
  }, [dispatch, employeeId])

  const defaultProps = useMemo(
    (): ActivityTimelineContextProps => ({
      card_id: employeeId,
      card_type: 'hr_employee',
      deleteTimelineItem: deleteTimelineItem,
      onEditHrEmployeeEvent,
    }),
    [employeeId, deleteTimelineItem, onEditHrEmployeeEvent],
  )

  // Преобразуем ошибку в строку, если это объект
  const errorMessage = useMemo(() => {
    if (!timelineError) return undefined
    if (typeof timelineError === 'string') return timelineError
    if (timelineError instanceof Error) return timelineError.message
    if (typeof timelineError === 'object' && timelineError.message) {
      return String(timelineError.message)
    }
    return 'Произошла ошибка при загрузке данных'
  }, [timelineError])

  // Защита: проверяем, что items - это массив и валидируем структуру данных
  const safeItems = useMemo(() => {
    if (!timelineData?.items) {
      return []
    }
    if (!Array.isArray(timelineData.items)) {
      console.warn('Timeline items is not an array:', timelineData.items)
      return []
    }
    // Валидируем структуру каждого элемента
    return timelineData.items.filter(item => {
      if (!item || typeof item !== 'object') {
        console.warn('Invalid timeline item:', item)
        return false
      }
      // Проверяем, что date существует и может быть преобразована в число
      if (item.date === undefined || item.date === null) {
        console.warn('Timeline item missing date:', item)
        return false
      }
      // Проверяем, что items внутри элемента - это массив
      if (item.items && !Array.isArray(item.items)) {
        console.warn('Timeline item.items is not an array:', item)
        return false
      }
      return true
    })
  }, [timelineData?.items])

  return (
    <Suspense fallback={<div>Загрузка...</div>}>
      <ActivityTimelineContext.Provider value={defaultProps}>
        <ActivityTimeline
          isLoading={loading || timelineIsLoading}
          items={safeItems}
          error={errorMessage}
          emptyMessage="Нет событий"
        />
        <EditHrEmployeeEventModal
          open={!!editEvent}
          employeeId={employeeId}
          initial={editEvent}
          onClose={() => setEditEvent(null)}
          onSaved={handleEventSaved}
        />
      </ActivityTimelineContext.Provider>
    </Suspense>
  )
})

TabTimeline.displayName = 'TabTimeline'
